import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import { useEffect, useMemo, useRef, useState } from "react";
import Input from "components/Input";
import InfoIcon from "assets/icons/error.svg?react";
import Button from "components/Button";
import { Context, Hub } from "react-signalr/src/signalr/types";
import { useLocation, useParams } from "react-router-dom";
import RefreshIcon from "assets/icons/reverse.svg?react";
import SortOrder from "components/SortOrder";
import BackIcon from "assets/icons/back.svg?react";
import axios from "axios";
import {
  EOrder,
  ETypeOfOrder,
  IOrderObject,
} from "components/SortOrder/typings";
import {
  Tooltip,
} from "@mui/material";
import classNames from "classnames";
import NotaIcon from "assets/icons/nota.svg?react";
import { IChatKeys, IRecovery } from "./typings";
import PanelsLayout from "components/PanelsLayout";
import { PackageList } from "modules/packageModule/packageList";
import { useInfiniteQuery } from "react-query";
import { axiosInstance } from "api/axios";
import { logger } from "utils/logger";
import { IPackage } from "dto/IPackage";
import { useContext } from "react";
import PackagesContext from "contexts/PackagesContext";
import KeysContext from "contexts/KeysContext";
import contactFormService from "api/contactFormService";
import { CircularProgress } from "@mui/material";
import CryptoJS from "crypto-js";
import EmptyPanel from "components/EmptyPanel";
import ChatCard from "components/CaseChatCard";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import CloseIcon from "assets/icons/close.svg?react";
import QrCodeIcon from "assets/icons/qr2.svg?react";
import CreateContactMessageModal from "components/CreateContactMessageModal";
import { IAttachment } from "dto/IAttachment";
import ShowPreviewModal from "components/ShowPreviewModal";
import usePDF from "hooks/usePDF";
import { IStoredKeys } from "dto/ContactForm/IStoredKeys";
import { QRCodeCanvas } from "qrcode.react";
import ReactDOM from "react-dom";
import CopyIcon from "assets/icons/copy.svg?react";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import MetricModal from "components/MetricModal";
import { decodeHash, StorageData } from "hooks/useStorageBridge";
import { useMediaQuery } from "usehooks-ts";
import { generateKey } from "utils/generateKey";

const ContactFormModule = ({
  SignalRContext,
}: {
  SignalRContext: Context<Hub<string, string>>;
}) => {
  const { t } = useTranslation();

  const isMobile = useMediaQuery("(max-width: 767px)");

  const { token } = useParams();
  const separatorRef = useRef();

  const { hash } = useLocation();
  const hashValue = hash?.slice(1); // strip leading #
  const [isLocalStoreBridge] = useState<boolean>(
    token === "bridge",
  );
  const [isInitialized, setIsInitialized] = useState(false);
  const [page, setPage] = useState(0);
  const { packages, setPackages } = useContext(PackagesContext);
  const { keys, setKeys } = useContext(KeysContext);

  const {
    data: chatData,
    isLoading: isChatLoading,
    isLoadingError: isChatLoadingError,
    refetch: refetchChat,
  } = contactFormService.useGetChat(
    token,
    SignalRContext?.connection?.connectionId,
    !!token && token !== "bridge",
  );
  const chat = useMemo(() => chatData?.data, [chatData]);
  const {
    blob,
    removeBlob,
    create,
    isNotSupported,
    setIsNotSupported,
  } = usePDF(token, chat?.Id, true);
  const [chatKeys, setChatKeys] = useState<IChatKeys>();
  const [restoreKeys, setRestoreKeys] = useState(false);

  const [recoveryKey, setRecoveryKey] = useState(hashValue);
  const [error, setError] = useState("");

  const [, setSelectedElements] = useState<IPackage[]>([]);
  const [canSelect] = useState(false);
  const [previewPackage, setPreviewPackage] = useState<IPackage>();
  const [createParcel, setCreateParcel] = useState(false);
  const [showMetric, setShowMetric] = useState(false);
  const [regenerateKeyMode, setRegenerateKeyMode] = useState(false);

  const [listFilter, setListFilter] = useState<IOrderObject>({
    type: ETypeOfOrder.SEND_DATE,
    order: EOrder.DESCENDING,
    initalized: false,
  });

  const SECRET_KEY = import.meta.env.VITE_APP_SECRET_KEY; // from .env

  const encryptLocalStore = (data: unknown) => {
    const json = JSON.stringify(data);
    return CryptoJS.AES.encrypt(json, SECRET_KEY).toString();
  };

  const decryptLocalStore = (ciphertext: string) => {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  };

  const qrCodeRef = useRef(null);
  const [showQr, setShowQr] = useState<boolean>(false);

  const handleRecoveryKeys = async () => {
    setError("");
    try {
      if (!chat.RecoveryPackage) {
        throw new Error("No Package");
      }

      const recovery = await CryptoJS.AES.decrypt(
        chat.RecoveryPackage,
        recoveryKey,
      );
      const encryptedPackage: IRecovery = JSON.parse(
        recovery.toString(CryptoJS.enc.Utf8),
      );

      setChatKeys({
        Private: encryptedPackage.private,
        Public: chat.PublicKey,
        Enc: recoveryKey,
      });

      localStorage.setItem(
        chat.Id,
        encryptLocalStore({
          Private: encryptedPackage.private,
          Public: chat.PublicKey,
          Key: recoveryKey,
        } as IStoredKeys),
      );

      setRestoreKeys(false);
    } catch (e) {
      console.error(e);
      setError(t("validation.invalidRecoveryKey"));
    }
  };

  const handleAskForRecovery = async () => {
    const initPayload: any = {
      AttachmentsCount: 0,
      Token: token,
    };

    const response = await axios.post(
      import.meta.env.VITE_APP_API_URL + "/contactform/init",
      initPayload,
      {
        withCredentials: true,
      },
    );

    if (!response?.data?.PublicKeys) {
      return;
    }

    // wygenerowanie klucza nadawcy oraz pakietu przywracania
    const senderKey = await generateKey("");

    const encryptionKey = Array(32)
      .fill("0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ")
      .map(
        (x) =>
          x[
            Math.floor(
              (crypto.getRandomValues(new Uint32Array(1))[0] /
                (0xffffffff + 1)) *
                x.length,
            )
          ],
      )
      .join("");

    setRecoveryKey(encryptionKey);

    const toEncrypt = JSON.stringify({ private: senderKey.PrivateKey });

    const recovery = CryptoJS.AES.encrypt(toEncrypt, encryptionKey).toString();

    const forgetPayload: any = {
      Token: token,
      PublicKey: senderKey.PublicKey,
      RecoveryPackage: recovery,
    };

    await axios.post(
      import.meta.env.VITE_APP_API_URL + "/contactform/chat/forget",
      forgetPayload,
      {
        withCredentials: true,
      },
    );

    const toSave: IStoredKeys = {
      Public: senderKey.PublicKey,
      Private: senderKey.PrivateKey,
      Key: encryptionKey,
    };

    const toSaveJ = JSON.stringify(toSave);
    const toSaveE = CryptoJS.AES.encrypt(
      toSaveJ,
      import.meta.env.VITE_APP_SECRET_KEY,
    ).toString();

    if (chat?.Id) {
      localStorage.setItem(chat?.Id, toSaveE);
    }

    setChatKeys({
      Enc: encryptionKey,
      Private: senderKey.PrivateKey,
      Public: senderKey.PublicKey,
    });

    setRestoreKeys(false);
    setRegenerateKeyMode(true);
  };

  useEffect(() => {
    if (
      chat &&
      chat.RecoveryPackage &&
      recoveryKey &&
      recoveryKey.length === 32
    ) {
      handleRecoveryKeys();
    }
  }, [chat]);

  const {
    data,
    fetchNextPage,
    fetchPreviousPage,
    isLoading,
    isFetchingNextPage,
    isFetchingPreviousPage,
    refetch,
    isFetching,
  } = useInfiniteQuery(
    ["packages"],
    async () => {
      const query = new URLSearchParams(window.location.search);

      query.append("token", token);
      query.append("SortProp", listFilter.type);
      query.append("SortOrder", listFilter.order);
      setPage(page + 1);

      const res = await axiosInstance.get(
        `/contactForm/chat/messages?${query}`,
      );

      return res.data;
    },
    {
      enabled: !!token,
      staleTime: 0,
      cacheTime: 0,
      getPreviousPageParam: (firstPage) => {
        if (firstPage?.PrevPageToken) {
          return firstPage.PrevPageToken;
        }
        return undefined;
      },
      getNextPageParam: (lastPage) => {
        if (!lastPage.LastPage) {
          return lastPage.PageToken;
        }
        return undefined;
      },
    },
  );

  useEffect(() => {
    if (!listFilter.initalized) return;
    if (!isInitialized) return;

    setPackages([]);
    refetch();
  }, [listFilter]);

  SignalRContext.useSignalREffect(
    "newMessage",
    () => {
      setPackages([]);
      refetch();
    },
    [],
  );

  useEffect(() => {
    refetchChat();
  }, [SignalRContext.connection.connectionId]);

  const compileData = async () => {
    logger.log("compile data called");
    if (!data) {
      logger.log("no data");
      return;
    }

    const packs = await Promise.all(
      data?.pages
        .map((page) => {
          return page.Items;
        })
        .flat(),

      // .sort((left: IPackage, right: IPackage) => {
      //   //@ts-ignore
      //   return right.Unread - left.Unread;
      // })
    );

    const newMap = packs.map((p: IPackage) => {
      const oldPack = packages.find((a) => a.Id === p.Id);

      if (oldPack?.isDelete) {
        p.isDelete = true;
      }

      return p;
    });

    const objectKeys = await Promise.all(
      data?.pages
        .map((page) => {
          return page.ObjectKeys;
        })
        .flat(),
    );

    const newPackages = newMap.flat();
    setPackages(newPackages);
    // @ts-ignore
    setKeys((prev) => [...prev, ...objectKeys]);
    setIsInitialized(true);

    if (!previewPackage && newPackages.length && !isMobile) {
      setPreviewPackage(newPackages[0]);
    }
  };

  const handleMessageClick = (p: IPackage) => {
    setPreviewPackage(p);
  };

  const handleMessageAttachmentPreview = (p: IPackage, a: IAttachment) => {
    create(p.Id, a, p.Kind);
  };

  const { download } = usePDF(token, chat?.Id, true);
  const onDownload = async (
    pack: IPackage,
    attachment: IAttachment,
  ) => {
    await download(pack.Id, attachment, pack.Kind);

    return;
  };

  useEffect(() => {
    if (isFetching) return;
    logger.log("Data changed", data);
    compileData();
  }, [data, isFetching]);

  useEffect(() => {
    if (qrCodeRef?.current) {
      const qrData =
        import.meta.env.VITE_APP_FORM_SITE +
        `/contactForm/${token}#${chatKeys.Enc}`;

      ReactDOM.render(
        <QRCodeCanvas value={qrData} size={200} />,
        qrCodeRef?.current,
      );
    }
  }, [qrCodeRef, showQr]);

  useEffect(() => {
    if (chat) {
      const storedKeysStr = localStorage.getItem(chat.Id);

      if (storedKeysStr) {
        try {
          const storedKeys = decryptLocalStore(storedKeysStr) as IStoredKeys;

          if (storedKeys != null) {
            setRestoreKeys(false);
            setChatKeys({
              Private: storedKeys.Private,
              Public: storedKeys.Public,
              Enc: storedKeys.Key,
            });
          } else {
            setRestoreKeys(true);
          }
        } catch {
          setRestoreKeys(true);
        }
      } else {
        setRestoreKeys(true);
      }
    }
  }, [chat]);

  // docelowo eszyfrowane.pl
  const PARENT_ORIGIN = "https://kontakt.notaup.pl";

  type BridgePayload =
    | { action: "read"; keys: string[] }
    | { action: "write"; payload: StorageData };

  useEffect(() => {
    if (isLocalStoreBridge) {
      if (!window.opener) {
        // Opened directly in browser tab — nothing to do
        return;
      }

      const postError = (message: string) => {
        window.opener.postMessage(
          { type: "BRIDGE_ERROR", message },
          PARENT_ORIGIN,
        );
        window.close();
      };

      let parsed: BridgePayload;

      // ── 1. Parse hash ──────────────────────────────────────────────────────
      const rawHash = hashValue;

      if (!rawHash) {
        postError("Bridge opened with empty hash.");
        return;
      }

      try {
        parsed = decodeHash(rawHash) as BridgePayload;
      } catch {
        postError("Failed to decode hash — invalid base64 or JSON.");
        return;
      }

      if (!parsed || typeof parsed !== "object" || !("action" in parsed)) {
        postError("Hash payload missing 'action' field.");
        return;
      }

      // ── 2. Execute action ──────────────────────────────────────────────────
      try {
        // if (parsed.action === "read") {
        //   const { keys } = parsed;

        //   if (!Array.isArray(keys) || keys.some((k) => typeof k !== "string")) {
        //     postError("'keys' must be an array of strings.");
        //     return;
        //   }

        //   const result: StorageData = {};
        //   for (const key of keys) {
        //     result[key] = localStorage.getItem(key);
        //   }

        //   window.opener.postMessage(
        //     { type: "BRIDGE_RESPONSE", payload: result },
        //     PARENT_ORIGIN
        //   );

        // } else
        if (parsed.action === "write") {
          const { payload } = parsed;

          if (
            !payload ||
            typeof payload !== "object" ||
            Array.isArray(payload)
          ) {
            postError("'payload' must be a key-value object.");
            return;
          }

          for (const [key, value] of Object.entries(payload)) {
            if (value === null) {
              localStorage.removeItem(key);
            } else if (typeof value === "string") {
              localStorage.setItem(key, value);
            } else {
              postError(
                `Invalid value type for key "${key}" — must be string or null.`,
              );
              return;
            }
          }

          window.opener.postMessage(
            { type: "BRIDGE_WRITE_ACK" },
            PARENT_ORIGIN,
          );
        } else {
          postError(`Unknown action: ${(parsed as { action: string }).action}`);
          return;
        }
      } catch (e) {
        // localStorage can throw in private browsing or when storage quota is exceeded
        postError(`localStorage operation failed: ${String(e)}`);
        return;
      }

      window.close();
    }
  }, []);

  const leftPanelContent = () => {
    return (
      <div className={styles.leftWrapper}>
        <div className={styles.order}>
          <SortOrder
            isLoading={
              isLoading || isFetchingNextPage || isFetchingPreviousPage
            }
            refetch={() => {
              // setPackages([]);
              // refetch();
            }}
            setListFilter={setListFilter}
            type={listFilter.type}
            order={listFilter.order}
          />
          <div className={styles.buttons}>
            <Tooltip title={t("common.refresh")}>
              <div
                className={styles.refreshButton}
                onClick={() => {
                  setPackages([]);
                  refetch();
                }}
              >
                <RefreshIcon></RefreshIcon>
              </div>
            </Tooltip>
          </div>
        </div>
        <PackageList
          className={styles.list}
          status={null}
          listFilter={null}
          separatorRef={separatorRef}
          handleScroll={() => {}}
          canSelect={canSelect}
          debouncedText={null}
          firstElement={null}
          isIncoming={false}
          onDownload={onDownload}
          searchText={null}
          selectMultiple={false}
          selectedElements={previewPackage ? [previewPackage] : []}
          setSelectedElements={setSelectedElements}
          showSomething={true}
          isLoading={isLoading}
          remove={() => {}}
          isInitialized={isInitialized}
          fetchNextPage={fetchNextPage}
          fetchPrevPage={fetchPreviousPage}
          isFirstLoading={isLoading && !isFetchingNextPage}
          newPackagesCounter={0}
          onMessageClick={handleMessageClick}
          onPreview={handleMessageAttachmentPreview}
          showBadgeOnIncomingMessage={true}
          isMobile={isMobile}
        />
        <div className={classNames(styles.btn, styles.bottombtn)}>
          <Button
            className={styles.btnfull}
            onClick={() => setCreateParcel(true)}
            element={
              <div className={styles.btnc}>
                <SendOutlinedIcon></SendOutlinedIcon> {t("modals.newMessage")}
              </div>
            }
          ></Button>
        </div>
      </div>
    );
  };

  const rightPanelContent = () => {
    return (
      <>
        {!previewPackage && <EmptyPanel></EmptyPanel>}
        {previewPackage && (
          <div className={styles.rightWrapper}>
            <ChatCard
              Package={previewPackage}
              keys={keys}
              onDownload={onDownload}
              onPreview={handleMessageAttachmentPreview}
              statuses={[]}
              tags={[]}
              index={0}
              refetch={() => {}}
              forceExpanded={true}
              grow={true}
              contactMessageChatId={chat?.Id}
              showReceiver={false}
              showTools={false}
              isMobile={isMobile}
            ></ChatCard>
          </div>
        )}
      </>
    );
  };

  if (isLocalStoreBridge) {
    return <div>{t("recovery.preparing")}</div>;
  }

  return (
    <div className={styles.root}>
      {<div className={styles.bgl}></div>}
      {<div className={styles.bgr}></div>}

      {
        <div className={styles.ourlogo}>
          Powered by <NotaIcon></NotaIcon>
        </div>
      }

      {isChatLoading && (
        <div className={styles.loading}>
          <CircularProgress />
        </div>
      )}

      {isChatLoadingError && (
        <div className={styles.fail}>
          <div className={styles.ico}>
            <ErrorOutlineOutlinedIcon></ErrorOutlineOutlinedIcon>
          </div>
          <div className={styles.msg}>{t("errors.conversationNotFound")}</div>
        </div>
      )}

      {restoreKeys && (
        <>
          <div className={styles.stack}>
            <div
              className={classNames(styles.form, styles.small, styles.recovery)}
            >
              <div className={styles.card}>
                <div className={styles.title2}>{t("recovery.codeTitle")}</div>
                <div className={styles.hint}>{t("recovery.codeHint")}</div>
                <div className={styles.input}>
                  <Input
                    label={t("recovery.codeInput")}
                    onChange={(text) => setRecoveryKey(text)}
                    value={recoveryKey}
                    variant="outlined"
                    className={styles.inp}
                    size="medium"
                    customBg="#f7f2f7"
                  />
                  {!!error.length && (
                    <div className={styles.error}>{error}</div>
                  )}
                </div>

                <div className={styles.btnWrapper}>
                  <Button
                    disabled={recoveryKey.length < 32 || isLoading}
                    className={styles.btnfull}
                    text={t("recovery.goToEncrypted")}
                    onClick={handleRecoveryKeys}
                  />
                </div>
                <div className={styles.recov}>
                  Nie pamiętasz kodu?{" "}
                  <a onClick={handleAskForRecovery}>
                    Kliknij aby poprosić o dostęp
                  </a>
                </div>
              </div>
            </div>
            <Tooltip title={t("tooltips.whereToFindCodeHint")}>
              <div className={styles.help}>
                <a>{t("tooltips.whereToFindCode")}</a>
              </div>
            </Tooltip>
          </div>
        </>
      )}

      {regenerateKeyMode && (
        <>
          <div className={styles.stack}>
            <div
              className={classNames(styles.form, styles.small, styles.recovery)}
            >
              <div className={styles.card}>
                <div className={styles.title2}>
                  Oczekiwanie na aktywację kodu konwersacji
                </div>
                <div className={styles.hint}>
                  Wysłaliśmy prośbę do użytkownika o wygenerowanie nowego kodu
                  konwersacji lub przywrócenie dostępu do konwersacji.
                  <br></br>Powiadomimy Cię, gdy użytkownik aktywuje nowy kod i
                  konwersacja będzie ponownie dostępna.
                </div>

                <div className={styles.btnWrapper}>
                  <Button
                    className={styles.btnfull}
                    text={t("common.close")}
                    onClick={() => setRegenerateKeyMode(false)}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {chat && chatKeys && !regenerateKeyMode && (
        <div className={classNames(styles.form, styles.large)}>
          <div
            className={classNames(
              styles.menu,
              isMobile && previewPackage ? styles.withBack : undefined,
            )}
          >
            <div className={styles.columns}>
              {isMobile && previewPackage && (
                <div className={styles.tools}>
                  <div className={styles.backButton}>
                    <BackIcon
                      onClick={() => setPreviewPackage(null)}
                    ></BackIcon>
                  </div>
                </div>
              )}
              <div className={styles.info}>
                <div className={styles.r1}>
                  {chat?.Label}
                  {/* <SealIcon></SealIcon> */}
                </div>
                <div className={styles.r2}>
                  {t("recovery.encryptedChannel")}
                </div>
              </div>
              {isMobile && previewPackage && (
                <div className={styles.tools}>
                  <Tooltip title={t("tooltips.metrics")}>
                    <div>
                      <InfoIcon
                        onClick={() => setShowMetric(!showMetric)}
                        className={styles.icon}
                      />
                    </div>
                  </Tooltip>
                </div>
              )}
            </div>
            {(!isMobile || !previewPackage) && (
              <Tooltip title={t("modals.conversationCode")}>
                <div
                  className={styles.qrbutton}
                  onClick={() => setShowQr(true)}
                >
                  <div className={styles.inner}>
                    <QrCodeIcon></QrCodeIcon>
                  </div>
                </div>
              </Tooltip>
            )}
          </div>
          <div className={styles.content}>
            {isMobile && !previewPackage && leftPanelContent()}
            {isMobile && previewPackage && rightPanelContent()}
            {!isMobile && (
              <PanelsLayout
                leftClassName={styles.panelsLeft}
                leftPanel={leftPanelContent()}
                rightPanel={rightPanelContent()}
                disableHeader={true}
              ></PanelsLayout>
            )}
          </div>
        </div>
      )}

      {createParcel && (
        <>
          <div className={styles.modalBg}></div>
          <div className={styles.modalWrapper}>
            <CreateContactMessageModal
              chatToken={token}
              publicKey={chatKeys?.Public}
              toggle={() => setCreateParcel(false)}
              onSent={() => {
                setPage(0);
                setIsInitialized(false);
                refetch();
              }}
              isMobile={isMobile}
              defaultTitle={
                packages.length ? packages[packages.length - 1].S1 : undefined
              }
            ></CreateContactMessageModal>
          </div>
        </>
      )}

      {blob && (
        <ShowPreviewModal toggle={removeBlob} blob={blob} isMobile={isMobile} />
      )}

      {isNotSupported && (
        <>
          <div className={styles.modalBg}></div>
          <div className={styles.modalWrapper}>
            <div
              className={classNames(
                styles.modal,
                styles.small,
                styles.notsupported,
              )}
            >
              <div className={styles.title}>
                {t("errors.unsupportedFormat")}
                <CloseIcon
                  className={styles.closeIcon}
                  onClick={(e) => {
                    setIsNotSupported(false);
                    e.stopPropagation();
                  }}
                />
              </div>
              <div className={styles.hint}>
                {t("errors.unsupportedFormatHint")}
              </div>
            </div>
          </div>
        </>
      )}

      {showQr && (
        <>
          <div className={styles.modalBg}></div>
          <div className={styles.modalWrapper}>
            <div className={classNames(styles.modal, styles.qrmodal)}>
              <div className={styles.title}>
                {t("modals.conversationCode")}
                <CloseIcon
                  className={styles.closeIcon}
                  onClick={(e) => {
                    setShowQr(false);
                    e.stopPropagation();
                  }}
                />
              </div>
              <div className={styles.modalContent}>
                <div className={styles.hint}>
                  {t("recovery.codeExplanation")}
                </div>
                <div className={styles.qrCodeWrapper}>
                  <div className={styles.qrcode} ref={qrCodeRef}></div>
                </div>
                <div className={styles.keyBox}>
                  <div className={styles.key}>{chatKeys.Enc}</div>
                  <div
                    className={styles.copy}
                    onClick={() => navigator.clipboard.writeText(chatKeys.Enc)}
                  >
                    <Tooltip title={t("common.copy")}>
                      <div>
                        <CopyIcon></CopyIcon>
                      </div>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {isMobile && showMetric && (
        <MetricModal toggle={setShowMetric} pack={previewPackage} />
      )}
    </div>
  );
};

export default ContactFormModule;
